import { lazyMarkReadOnTableViewChange } from 'owa-mail-mark-read-actions';
import type { TableView } from 'owa-mail-list-store';
import {
    type TableQueryType,
    getViewFilterForTable,
    isUnreadSearchFolder,
} from 'owa-mail-list-store';
import { lazyCleanSearchTableState } from 'owa-mail-execute-search-actions';
import tombstoneOperations from 'owa-mail-list-tombstone';
import { shouldShowReadingPane } from 'owa-mail-layout';
import { setItem } from 'owa-local-storage';
import updatePrioritizeRequestStatusNudgedMutator from 'owa-mail-priority-inbox-store/lib/mutators/updatePrioritizeRequestStatusNudgedMutator';
import folderIdToName from 'owa-session-store/lib/utils/folderIdToName';
import { getMailboxInfo } from 'owa-mail-mailboxinfo';
import { isFeatureEnabled } from 'owa-feature-flags';

/**
 * Called after loading the new table. The reason we are passing the selectedRowKeys
 * and not reading them from the oldTableView is because the reset selection has already
 * been called in onBeforeTableLoad and it has to happen synchronously before the new
 * table loads else we end up in not resetting the LV/RP views in certain scenarios.
 * @param selectedRowKeysForOldTable the selectedRowKeys for the old tableview
 * @param oldTableView the old table view
 * @param newTableView the new tableView we are switching to
 */
export async function onAfterTableLoad(
    selectedRowKeysForOldTable: string[],
    oldTableView: TableView,
    newTableView: TableView
): Promise<void> {
    // Clear entries from the tombstone for the old table
    if (oldTableView.tableQuery.type === 0) {
        tombstoneOperations.clearMapForFolderMutator(oldTableView.serverFolderId);
    }

    if (newTableView.tableQuery.type === 0) {
        const viewFilter = getViewFilterForTable(newTableView);
        if (viewFilter !== 'CopilotHighPriority') {
            setItem(window, 'mail_prioritizeRollupFilter', 'false');
        }
    } else {
        setItem(window, 'mail_prioritizeRollupFilter', 'false');
    }

    if (shouldMarkReadOnTableViewChange(selectedRowKeysForOldTable, newTableView)) {
        const markReadOnTableChange = await lazyMarkReadOnTableViewChange.import();
        markReadOnTableChange(selectedRowKeysForOldTable, oldTableView);
    }

    const folderName = folderIdToName(newTableView.tableQuery.folderId);
    const mailboxInfo = getMailboxInfo(newTableView);
    if (isFeatureEnabled('tri-prioritize-on-demand') && folderName === 'inbox') {
        updatePrioritizeRequestStatusNudgedMutator(mailboxInfo, newTableView);
    }

    // Clean the old table View's state.
    // Currently we do not have to clean up anything on a non-search (or Folder) table
    // as we are caching them and the only clean up that we perform is the resetting the selection
    if (oldTableView.tableQuery.type === 1 && oldTableView.id !== newTableView.id) {
        /**
         * The reason we want to wait on markReadOnTableChange promise is to make sure the
         * mark read operation completes correctly on the row that was selected in the search table,
         * before we try to clear the rows in the table
         */
        lazyCleanSearchTableState(oldTableView);
    }
}

/**
 * Return true if all of these conditions are met:
 * 1) Single row was selected in the old table
 * 2) Reading pane is shown
 * 3) Not moving to unread filter
 * */
function shouldMarkReadOnTableViewChange(
    oldSelectedRowKeys: string[],
    newTableView: TableView
): boolean {
    return (
        oldSelectedRowKeys.length == 1 &&
        shouldShowReadingPane() &&
        getViewFilterForTable(newTableView) !== 'Unread' &&
        !isUnreadSearchFolder(newTableView)
    );
}
